import React from 'react'
import styles from './ReNegotiationView.module.scss'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useAuthStatus } from '../../../utils'
import { useNavigate, useParams } from 'react-router-dom'
import { useOfferDetail, useRenegotiateOffer } from '../../../hooks'
import { useToken } from '../../../utils/auth'
import GeneralContractInfoSubmitForm from '../create/GeneralContractInfoSubmitForm'
import DomesticContractInfoSubmitForm from '../create/DomesticContractInfoSubmitForm'
import OverseasContractInfoSubmitForm from '../create/OverseasContractInfoSubmitForm'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { Tooltip } from 'react-tooltip'
import { ArrowLeftIcon } from '@heroicons/react/24/solid'
import { ExclamationCircleIcon } from '@heroicons/react/24/solid'

const ReNegotiationView = () => {
	const navigate = useNavigate()
	const { offerId } = useParams()
	const [authStatus] = useAuthStatus()
	const token = useToken()
	const { data: offerData, isLoading, error } = useOfferDetail(offerId)
	const { mutate: renegotiateOfferMutation, isLoading: isSubmitting } = useRenegotiateOffer()
	const [prevData, setPrevData] = useState(null)
	const [noDataChangedMessageOpen, setNoDataChangedMessageOpen] = useState(false)
	const [hasChanges, setHasChanges] = useState(false)
	const [formValidations, setFormValidations] = useState({
		general: false,
		domestic: false,
		overseas: false,
	})

	// 폼 refs
	const generalFormRef = useRef()
	const domesticFormRef = useRef()
	const overseasFormRef = useRef()

	const handleNoDataChanged = () => {
		setNoDataChangedMessageOpen(true)
		setTimeout(() => {
			setNoDataChangedMessageOpen(false)
		}, 3000)
	}

	// 깊은 비교 함수 (속성 순서 무관)
	const deepEqual = useCallback((obj1, obj2) => {
		if (obj1 === obj2) return true
		if (obj1 == null || obj2 == null) return false
		if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return obj1 === obj2

		const keys1 = Object.keys(obj1)
		const keys2 = Object.keys(obj2)

		if (keys1.length !== keys2.length) return false

		for (let key of keys1) {
			if (!keys2.includes(key)) return false
			if (!deepEqual(obj1[key], obj2[key])) return false
		}

		return true
	}, [])

	// 간단한 데이터 변환 함수 (API 형식에 맞게 수정)
	const convertToApiFormat = useCallback((formData, contractType) => {
		const baseMapping = {
			isSalaryTaxIncluded: formData.applyTax === 'include',
			payrollCycles: formData.payPeriod === 'weekly' ? 'weekly' : 'perGame',
			wearSponsoredHat: formData.sponsorCap,
			prizeType: formData.incentiveTaxOption === 'amountDisplayed' ? 'displayed' : 'actual',
			incentives:
				formData.incentiveSettings?.map((incentive) => ({
					startRank: incentive.start,
					endRank: incentive.end === 'CUT' ? 0 : incentive.end,
					percentage: incentive.percentage,
				})) || [],
		}

		// 계약 타입별 추가 필드
		if (contractType === 'termContract') {
			return {
				...baseMapping,
				salaryAmountForPeriod: parseInt(formData.periodSalary) || 0,
				isOverseasIncluded: formData.overseasTour,
				expenseReimbursement: formData.overseasSupport,
			}
		}

		if (contractType === 'domesticContract') {
			return {
				...baseMapping,
				weeklySalaryCutThrough: parseInt(formData.cutPass) || 0,
				weeklySalaryCutFail: parseInt(formData.cutOut) || 0,
				jejuTournamentParticipation: formData.isTourJejudo,
				expenseReimbursementJeju: parseInt(formData.jejudoSupport) || 0,
			}
		}

		if (contractType === 'overseasContract') {
			return {
				...baseMapping,
				weeklySalaryCutThrough: parseInt(formData.cutPass) || 0,
				weeklySalaryCutFail: parseInt(formData.cutOut) || 0,
				expenseReimbursement: formData.overseasSupport,
			}
		}

		return baseMapping
	}, [])

	// 현재 폼 데이터 가져오기
	const getCurrentFormData = useCallback(() => {
		const newData = {}

		if (prevData?.termContract !== null) {
			newData.termContract = generalFormRef.current?.getFormData?.()
		}
		if (prevData?.domesticContract !== null) {
			newData.domesticContract = domesticFormRef.current?.getFormData?.()
		}
		if (prevData?.overseasContract !== null) {
			newData.overseasContract = overseasFormRef.current?.getFormData?.()
		}

		return newData
	}, [prevData])

	// 변경 여부 체크
	const checkForChanges = useCallback(() => {
		const currentData = getCurrentFormData()
		const convertedData = {}
		let hasChanges = false

		// 각 계약 타입별로 변환
		if (currentData.termContract) {
			convertedData.termContract = convertToApiFormat(
				currentData.termContract,
				'termContract',
			)
			// id 필드와 incentives의 id 필드들 제외하고 비교
			const { id, incentives: prevIncentives, ...prevTermWithoutId } = prevData.termContract
			const cleanPrevIncentives =
				prevIncentives?.map(({ id, termContractId, ...rest }) => rest) || []
			const prevTermClean = {
				...prevTermWithoutId,
				incentives: cleanPrevIncentives,
			}
			hasChanges = !deepEqual(prevTermClean, convertedData.termContract) || hasChanges
		}
		if (currentData.domesticContract) {
			convertedData.domesticContract = convertToApiFormat(
				currentData.domesticContract,
				'domesticContract',
			)
			// id 필드와 incentives의 id 필드들 제외하고 비교
			const {
				id,
				incentives: prevIncentives,
				...prevDomesticWithoutId
			} = prevData.domesticContract
			const cleanPrevIncentives =
				prevIncentives?.map(({ id, domesticContractId, ...rest }) => rest) || []
			const prevDomesticClean = {
				...prevDomesticWithoutId,
				incentives: cleanPrevIncentives,
			}
			console.log('prevData.domesticContract : ', JSON.stringify(prevDomesticClean))
			console.log(
				'convertedData.domesticContract : ',
				JSON.stringify(convertedData.domesticContract),
			)
			hasChanges = !deepEqual(prevDomesticClean, convertedData.domesticContract) || hasChanges
		}
		if (currentData.overseasContract) {
			convertedData.overseasContract = convertToApiFormat(
				currentData.overseasContract,
				'overseasContract',
			)
			// id 필드와 incentives의 id 필드들 제외하고 비교
			const {
				id,
				incentives: prevIncentives,
				...prevOverseasWithoutId
			} = prevData.overseasContract
			const cleanPrevIncentives =
				prevIncentives?.map(({ id, overseasContractId, ...rest }) => rest) || []
			const prevOverseasClean = {
				...prevOverseasWithoutId,
				incentives: cleanPrevIncentives,
			}
			hasChanges = !deepEqual(prevOverseasClean, convertedData.overseasContract) || hasChanges
		}

		console.log('hasChanges : ', hasChanges)

		return hasChanges
	}, [getCurrentFormData, convertToApiFormat, prevData])

	const handleRenegotiation = async () => {
		// 버튼 클릭 시에만 변경 여부 체크
		const hasChanges = checkForChanges()
		// console.log('prevData : ', prevData)
		// console.log('currentData : ', getCurrentFormData())
		// console.log('hasChanges : ', hasChanges)

		// 잠시 기다린 후 hasChanges 상태 확인
		// setTimeout(() => {
		// 	if (!hasChanges) {
		// 		handleNoDataChanged()
		// 		return
		// 	}

		// 	// 변경이 있으면 API 호출 진행
		// 	submitRenegotiation()
		// }, 100)

		if (!hasChanges) {
			handleNoDataChanged()
			return
		}

		submitRenegotiation()
	}

	const submitRenegotiation = async () => {
		try {
			const contract = {}

			// 각 계약 타입별로 폼 데이터 수집 및 변환
			if (prevData?.termContract !== null) {
				const formData = generalFormRef.current?.getFormData?.()
				contract.termContract = convertToApiFormat(formData, 'termContract')
			}
			if (prevData?.domesticContract !== null) {
				const formData = domesticFormRef.current?.getFormData?.()
				contract.domesticContract = convertToApiFormat(formData, 'domesticContract')
			}
			if (prevData?.overseasContract !== null) {
				const formData = overseasFormRef.current?.getFormData?.()
				contract.overseasContract = convertToApiFormat(formData, 'overseasContract')
			}

			// React Query 훅 사용
			renegotiateOfferMutation(
				{
					offerId: prevData.id,
					contractData: { contract },
					token,
				},
				{
					onSuccess: () => {
						console.log('재협상 제안 성공')

						navigate(-1)
					},
					onError: (error) => {
						console.error('재협상 제안 실패:', error)
						// if (error.response?.status === 400) {
						// 	handleNoDataChanged()
						// }
					},
				},
			)
		} catch (error) {
			console.error('재협상 제안 실패:', error)
		}
	}

	useEffect(() => {
		if (offerData) {
			console.log('offerData : ', offerData)
			setPrevData(offerData)
		}
	}, [offerData])

	// useEffect(() => {
	// 	if (
	// 		informationForDisplay.selectedToursData &&
	// 		informationForDisplay.selectedToursData.length > 0
	// 	) {
	// 		// 시작날짜가 가장 이른 것 찾기
	// 		const earliestStartDate = informationForDisplay.selectedToursData.reduce(
	// 			(earliest, tour) => {
	// 				return tour.startDate < earliest ? tour.startDate : earliest
	// 			},
	// 			informationForDisplay.selectedToursData[0].startDate,
	// 		)

	// 		// 끝날짜가 가장 늦은 것 찾기
	// 		const latestEndDate = informationForDisplay.selectedToursData.reduce((latest, tour) => {
	// 			return tour.endDate > latest ? tour.endDate : latest
	// 		}, informationForDisplay.selectedToursData[0].endDate)

	// 		// 상태 업데이트
	// 		setLongContractStartDate(earliestStartDate)
	// 		setLongContractEndDate(latestEndDate)

	// 		// 지역별 계약 타입 설정
	// 		informationForDisplay.selectedToursData.forEach((tour) => {
	// 			if (tour.region === 'DOMESTIC') {
	// 				setInformationForDisplay((prev) => ({
	// 					...prev,
	// 					isDomesticContract: true,
	// 				}))
	// 			} else if (tour.region === 'INTERNATIONAL') {
	// 				setInformationForDisplay((prev) => ({
	// 					...prev,
	// 					isOverseasContract: true,
	// 				}))
	// 			}
	// 		})
	// 	}
	// }, [informationForDisplay.selectedToursData])

	// validation 상태를 상위 컴포넌트로 전달 - useCallback으로 메모이제이션
	const handleValidationChange = useCallback((formType, isValid) => {
		setFormValidations((prev) => ({
			...prev,
			[formType]: isValid,
		}))
	}, [])

	// 버튼 활성화 조건 계산 (벨리데이션만 확인)
	const isButtonEnabled = useMemo(() => {
		// 현재 활성화된 폼들의 벨리데이션 확인
		if (prevData?.termContract !== null) {
			return formValidations.general
		}
		if (prevData?.domesticContract !== null && prevData?.overseasContract === null) {
			return formValidations.domestic
		}
		if (prevData?.overseasContract !== null && prevData?.domesticContract === null) {
			return formValidations.overseas
		}
		if (prevData?.domesticContract !== null && prevData?.overseasContract !== null) {
			return formValidations.domestic && formValidations.overseas
		}
		return false
	}, [formValidations, prevData])

	// 로딩 상태 처리
	if (isLoading) {
		return (
			<div className={styles.jobPostThirdPageContainer}>
				<div className={styles.title}>데이터를 불러오는 중...</div>
			</div>
		)
	}

	// 에러 상태 처리
	if (error) {
		return (
			<div className={styles.jobPostThirdPageContainer}>
				<div className={styles.title}>데이터를 불러오는데 실패했습니다.</div>
				<button onClick={() => navigate(-1)}>뒤로가기</button>
			</div>
		)
	}

	return (
		<div className={styles.jobPostThirdPageContainer}>
			<div className={styles.goBack} onClick={() => navigate(-1)}>
				<ArrowLeftIcon className={styles.goBackIcon} />
				<span className={styles.goBackText}>뒤로가기</span>
			</div>
			<div className={styles.title}>조건 재협상하기</div>
			<div className={styles.tourInformationDisplayWrapper}>
				{prevData?.termContract !== null && (
					<GeneralContractInfoSubmitForm
						ref={generalFormRef}
						onValidationChange={handleValidationChange}
						isOverseas={false}
						isReNegotiation={true}
						generalContractData={prevData?.termContract}
					/>
				)}
				{prevData?.domesticContract !== null && (
					<DomesticContractInfoSubmitForm
						ref={domesticFormRef}
						onValidationChange={handleValidationChange}
						isReNegotiation={true}
						domesticContractData={prevData?.domesticContract}
					/>
				)}
				{prevData?.overseasContract !== null && (
					<OverseasContractInfoSubmitForm
						ref={overseasFormRef}
						onValidationChange={handleValidationChange}
						isReNegotiation={true}
						overseasContractData={prevData?.overseasContract}
					/>
				)}
			</div>

			{/* 통합 버튼 */}
			{(prevData?.termContract !== null ||
				prevData?.domesticContract !== null ||
				prevData?.overseasContract !== null) && (
				<div className={styles.whatsNext}>
					{noDataChangedMessageOpen && (
						<div className={styles.noDataChangedMessage}>
							<ExclamationCircleIcon className={styles.noDataChangedMessageIcon} />
							변경된 항목이 없어요.
						</div>
					)}
					<div className={styles.whatsNextButtonContainer}>
						{/* <button
							type="button"
							onClick={() => navigate(-1)}
							className={styles.beforeButton}>
							이전
						</button> */}
						<button
							type="button"
							onClick={handleRenegotiation}
							disabled={!isButtonEnabled || isSubmitting}
							className={
								isButtonEnabled ? styles.nextButton : styles.nextButtonDisabled
							}>
							{isSubmitting ? '제안 중...' : '제안하기'}
						</button>
					</div>
				</div>
			)}
		</div>
	)
}
export default ReNegotiationView
